/**
 * Enhanced Megamenu - Custom Behavior Handler
 * Extends the existing megamenu system with configurable open/close behavior
 *
 * Supports:
 * - Open behavior: hover or click
 * - Close behavior: mouse leave or outside click
 */

class EnhancedMegamenu {
  constructor(container) {
    if (!container) return;

    this.container = container;
    this.settings = this.getSettings();
    this.enhancedMenuItems = [];
    this.activeMenuItem = null;
    this.clickOutsideHandler = null;
    this.scrollCloseHandler = null;

    this.resizeHandler = this.debounce(() => this.equalizePanelHeights(), 200);
    window.addEventListener("resize", this.resizeHandler);

    this.init();
  }

  /**
   * Get settings from the header section
   */
  getSettings() {
    const headerSection = document.querySelector(
      '[data-section-type="header"]'
    );
    if (!headerSection) {
      return {
        openBehavior: "hover",
        closeBehavior: "mouse_leave",
        closeOnScroll: false,
        panelHeight: "auto",
      };
    }

    // Settings are stored in data attributes or we use defaults
    return {
      openBehavior:
        headerSection.dataset.enhancedMegamenuOpenBehavior || "hover",
      closeBehavior:
        headerSection.dataset.enhancedMegamenuCloseBehavior || "mouse_leave",
      closeOnScroll:
        headerSection.dataset.enhancedMegamenuCloseOnScroll === "true",
      panelHeight:
        headerSection.dataset.enhancedMegamenuPanelHeight || "auto",
    };
  }

  /**
   * Initialize enhanced megamenu items
   */
  init() {
    // Find all menu items that have enhanced megamenu blocks
    const menuItems = this.container.querySelectorAll(".m-menu__item--mega");

    menuItems.forEach((menuItem) => {
      const enhancedMegamenu = menuItem.querySelector(".m-enhanced-megamenu");
      if (enhancedMegamenu) {
        this.addMenuItem(menuItem, enhancedMegamenu);
      }
    });

    // Setup outside click handler if needed
    if (this.settings.closeBehavior === "outside_click") {
      this.setupOutsideClickHandler();
    }

    // Setup scroll-close handler if enabled (additive - works with any closeBehavior)
    if (this.settings.closeOnScroll) {
      this.setupScrollCloseHandler();
    }
  }

  /**
   * Add a menu item to tracking
   */
  addMenuItem(menuItem, megamenu) {
    // Check if already tracked
    const exists = this.enhancedMenuItems.find((i) => i.menuItem === menuItem);
    if (exists) return;

    // Detach the panel from its SSR location so DOM-walking a11y helpers
    // (Avada zoom-on-hover, etc.) can't index its closed-state text. The
    // returned reference is reused by openMenu/closeMenu to attach/detach
    // the same DOM node on demand. See repositionMegamenu for the full
    // rationale.
    const repositionedMegamenu = this.repositionMegamenu(menuItem, megamenu);

    // Wire the ARIA disclosure contract on the trigger so assistive tech
    // (Avada Vision Impairment helpers, VoiceOver, NVDA, etc.) understands
    // the link both navigates AND opens a controlled region. Cached on the
    // per-item record so open/close toggles don't re-query the DOM.
    const triggerLink = menuItem.querySelector(".m-menu__link--main");
    if (triggerLink && repositionedMegamenu.id) {
      triggerLink.setAttribute("aria-haspopup", "true");
      triggerLink.setAttribute("aria-expanded", "false");
      triggerLink.setAttribute("aria-controls", repositionedMegamenu.id);
    }

    this.enhancedMenuItems.push({
      menuItem,
      megamenu: repositionedMegamenu,
      triggerLink,
      isOpen: false,
      // Pending detach timer scheduled by closeMenu and cancelled by
      // openMenu so a rapid re-hover doesn't strand a removed panel.
      detachTimeoutId: null,
    });

    this.attachEvents(menuItem, repositionedMegamenu, triggerLink);
  }

  /**
   * Detach the megamenu panel from the DOM at init.
   *
   * The panel is rendered server-side inside `.m-mega-menu`. We pull it out
   * entirely (no parent) and hold it only via the JS reference on
   * `enhancedMenuItems[i].megamenu`. `openMenu` reattaches it to the menu
   * item on demand, and `closeMenu` removes it again after the close
   * transition completes.
   *
   * Why detach instead of just hide via CSS: vision-impairment helpers
   * such as Avada zoom-on-hover walk the raw DOM (`textContent`) and
   * ignore CSS visibility/inert/aria-hidden/pointer-events. The only way
   * to keep them from indexing closed-panel links ("17 Francisco Trincão",
   * "5 - Hidemasa Morita", etc.) is to make sure those nodes are not
   * present in the document. The same DOM node is reused on every open,
   * so all event listeners attached via addEventListener are preserved.
   */
  repositionMegamenu(menuItem, megamenu) {
    if (megamenu.parentNode) {
      megamenu.parentNode.removeChild(megamenu);
    }
    return megamenu;
  }

  /**
   * Attach events based on open behavior
   */
  attachEvents(menuItem, megamenu, triggerLink) {
    const openBehavior = this.settings.openBehavior;
    const closeBehavior = this.settings.closeBehavior;

    // Mark this menu item as having enhanced behavior
    menuItem.setAttribute("data-enhanced-megamenu", "true");

    if (openBehavior === "click") {
      // Click to open - prevent default link behavior
      const menuLink =
        triggerLink || menuItem.querySelector(".m-menu__link--main");
      if (menuLink) {
        menuLink.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleMenu(menuItem);
        });
      }

      // Also prevent the default hover behavior from SiteNav
      menuItem.addEventListener(
        "mouseenter",
        (e) => {
          e.stopImmediatePropagation();
        },
        true
      );
    } else {
      // Hover to open - prevent SiteNav from handling it
      menuItem.addEventListener(
        "mouseenter",
        (e) => {
          e.stopImmediatePropagation();
          this.openMenu(menuItem);
        },
        true
      );

      // Also prevent SiteNav's mouseleave if we're using outside_click
      if (closeBehavior === "outside_click") {
        menuItem.addEventListener(
          "mouseleave",
          (e) => {
            e.stopImmediatePropagation();
          },
          true
        );
      }
    }

    // Keyboard support - works regardless of openBehavior so non-mouse users
    // (and Avada's keyboard-nav helper) can drive the disclosure. The trigger
    // remains a real <a> for click navigation; we only intercept the
    // disclosure keys (Enter/Space/Down to open, Escape to close).
    if (triggerLink) {
      triggerLink.addEventListener("keydown", (e) => {
        switch (e.key) {
          case "Enter":
          case " ":
          case "ArrowDown": {
            e.preventDefault();
            this.openMenu(menuItem);
            // Wait one frame so the panel transitions in before we focus.
            requestAnimationFrame(() => {
              const firstFocusable = megamenu.querySelector(
                "a, button, [tabindex]:not([tabindex='-1'])"
              );
              if (firstFocusable) firstFocusable.focus();
            });
            break;
          }
          case "Escape": {
            const item = this.enhancedMenuItems.find(
              (i) => i.menuItem === menuItem
            );
            if (item && item.isOpen) {
              e.preventDefault();
              this.closeMenu(menuItem);
              triggerLink.focus();
            }
            break;
          }
        }
      });
    }

    // Escape from inside the panel closes it and returns focus to the trigger.
    megamenu.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        this.closeMenu(menuItem);
        if (triggerLink) triggerLink.focus();
      }
    });

    // Tab-out closes the panel cleanly. `focusout` fires before the new
    // element receives focus, and `relatedTarget` is the next focus target;
    // closing only when it falls outside the menu item avoids closing while
    // the user is tabbing between links inside the panel.
    menuItem.addEventListener("focusout", (e) => {
      if (!menuItem.contains(e.relatedTarget)) {
        this.closeMenu(menuItem);
      }
    });

    // Handle close behavior
    if (closeBehavior === "mouse_leave") {
      let closeTimeout;

      // Close when leaving menu item
      menuItem.addEventListener("mouseleave", (e) => {
        // Check if mouse is moving to the megamenu
        closeTimeout = setTimeout(() => {
          // Only close if not hovering over megamenu
          if (!megamenu.matches(":hover")) {
            this.closeMenu(menuItem);
          }
        }, 100);
      });

      // Cancel close if entering megamenu
      megamenu.addEventListener("mouseenter", (e) => {
        clearTimeout(closeTimeout);
      });

      // Close when leaving megamenu
      megamenu.addEventListener("mouseleave", (e) => {
        setTimeout(() => {
          // Only close if not hovering over menu item
          if (!menuItem.matches(":hover")) {
            this.closeMenu(menuItem);
          }
        }, 100);
      });
    } else if (closeBehavior === "outside_click") {
      // For outside_click, do NOT attach any mouse leave handlers
      // The global click handler will take care of closing
      // This prevents the menu from closing on hover
    }
  }

  /**
   * Toggle menu open/close
   */
  toggleMenu(menuItem) {
    const item = this.enhancedMenuItems.find((i) => i.menuItem === menuItem);
    if (!item) return;

    if (item.isOpen) {
      this.closeMenu(menuItem);
    } else {
      // Close other menus first
      this.closeAllMenus();
      this.openMenu(menuItem);
    }
  }

  /**
   * Open menu
   */
  openMenu(menuItem) {
    const item = this.enhancedMenuItems.find((i) => i.menuItem === menuItem);
    if (!item || item.isOpen) return;

    // Cancel any pending detach left over from a recent closeMenu, otherwise
    // a rapid re-hover would see the panel disappear mid-transition.
    if (item.detachTimeoutId) {
      clearTimeout(item.detachTimeoutId);
      item.detachTimeoutId = null;
    }

    // Reattach the panel to its menu item. Force a synchronous layout read
    // (offsetWidth) so the closed-state CSS commits before the open-state
    // class flips opacity/visibility — without it the browser would coalesce
    // both states into one paint and the fade transition would be skipped.
    if (!menuItem.contains(item.megamenu)) {
      menuItem.appendChild(item.megamenu);
      // eslint-disable-next-line no-unused-expressions
      item.megamenu.offsetWidth;
    }

    // Close all other enhanced megamenus first
    this.closeAllMenus();

    // Add ONLY our custom class - don't affect header
    menuItem.classList.add("enhanced-menu-open");

    // Expose the panel to the accessibility tree and announce the disclosure
    // state on the trigger. `inert` is the modern equivalent of removing
    // tabindex/blocking pointer events while keeping content addressable;
    // `aria-hidden` mirrors it for older AT.
    item.megamenu.removeAttribute("inert");
    item.megamenu.setAttribute("aria-hidden", "false");
    if (item.triggerLink) {
      item.triggerLink.setAttribute("aria-expanded", "true");
    }

    // Remove transparent background from header when megamenu is open
    const header = this.container.closest(".m-header");
    if (header) {
      header.classList.add("enhanced-megamenu-active");
    }

    item.isOpen = true;
    this.activeMenuItem = menuItem;

    // Fix positioning for w-full megamenus
    this.fixFullWidthPosition(menuItem, item.megamenu);

    // Equalize menu heights
    this.equalizeMenuHeights(item.megamenu);

    // Trigger any sliders to update
    this.reInitSlider(menuItem);
  }

  /**
   * Fix positioning for full-width megamenus
   * Calculate the correct top position when using position: fixed
   */
  fixFullWidthPosition(menuItem, megamenu) {
    if (!megamenu.classList.contains("w-full")) return;

    // Get the menu item's position relative to viewport
    const menuItemRect = menuItem.getBoundingClientRect();

    // For position: fixed, we use the viewport-relative position
    // Add 1px gap to prevent overlap with header
    megamenu.style.top = menuItemRect.bottom + 1 + "px";
  }

  /**
   * Equalize heights of all menus within the megamenu
   */
  equalizeMenuHeights(megamenu) {
    // Find all menu elements within this megamenu
    const menus = megamenu.querySelectorAll(".m-enhanced-megamenu__menu");

    if (menus.length === 0) return;

    // Reset any previously set heights
    menus.forEach((menu) => {
      menu.style.minHeight = "";
    });

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      // Find the tallest menu
      let maxHeight = 0;
      menus.forEach((menu) => {
        const height = menu.offsetHeight;
        if (height > maxHeight) {
          maxHeight = height;
        }
      });

      // Apply the max height to all menus
      if (maxHeight > 0) {
        menus.forEach((menu) => {
          menu.style.minHeight = maxHeight + "px";
        });
      }
    });
  }

  /**
   * When `panelHeight: "match_tallest"` is configured, normalise every
   * cached panel's height so the user sees a consistent panel size as
   * they hover between triggers. Skipped entirely in "auto" mode.
   *
   * Preserves the Avada detach contract: each panel is briefly appended
   * to its menu item with the existing closed-state CSS (visibility:hidden,
   * opacity:0, pointer-events:none, inert), we read offsetHeight
   * synchronously, then removeChild immediately — no paint frame between
   * the two, so Avada zoom-on-hover never indexes the text.
   */
  equalizePanelHeights() {
    if (this.settings.panelHeight !== "match_tallest") return;
    if (this.enhancedMenuItems.length === 0) return;

    let maxHeight = 0;

    this.enhancedMenuItems.forEach((item) => {
      const wasAttached = item.megamenu.parentNode === item.menuItem;
      if (!wasAttached) item.menuItem.appendChild(item.megamenu);

      const previousMinHeight = item.megamenu.style.minHeight;
      item.megamenu.style.minHeight = "";
      const height = item.megamenu.offsetHeight;
      item.megamenu.style.minHeight = previousMinHeight;

      if (!wasAttached && item.megamenu.parentNode) {
        item.megamenu.parentNode.removeChild(item.megamenu);
      }

      if (height > maxHeight) maxHeight = height;
    });

    if (maxHeight <= 0) return;

    this.enhancedMenuItems.forEach((item) => {
      item.megamenu.style.minHeight = maxHeight + "px";
    });
  }

  /**
   * Simple debounce helper for resize handlers.
   */
  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  }

  /**
   * Close menu
   */
  closeMenu(menuItem) {
    const item = this.enhancedMenuItems.find((i) => i.menuItem === menuItem);
    if (!item || !item.isOpen) return;

    // Remove ONLY our custom class
    menuItem.classList.remove("enhanced-menu-open");

    // Mirror the open-state ARIA toggles in reverse so AT and Avada know the
    // panel is collapsed again and stop announcing it as the active region.
    item.megamenu.setAttribute("inert", "");
    item.megamenu.setAttribute("aria-hidden", "true");
    if (item.triggerLink) {
      item.triggerLink.setAttribute("aria-expanded", "false");
    }

    item.isOpen = false;
    if (this.activeMenuItem === menuItem) {
      this.activeMenuItem = null;
    }

    // Check if any enhanced megamenu is still open
    const anyOpen = this.enhancedMenuItems.some((i) => i.isOpen);

    // If no enhanced megamenu is open, restore transparent background
    if (!anyOpen) {
      const header = this.container.closest(".m-header");
      if (header) {
        header.classList.remove("enhanced-megamenu-active");
      }
    }

    // Detach the panel from the DOM after the close transition completes
    // (CSS is 0.3s; 350ms gives a small safety margin). Cancelled by
    // openMenu if the user re-hovers in time. Removing the panel from the
    // document is the only way to keep DOM-walking a11y helpers (Avada
    // zoom-on-hover, etc.) from indexing the closed panel's link text.
    if (item.detachTimeoutId) clearTimeout(item.detachTimeoutId);
    item.detachTimeoutId = setTimeout(() => {
      item.detachTimeoutId = null;
      if (!item.isOpen && item.megamenu.parentNode) {
        item.megamenu.parentNode.removeChild(item.megamenu);
      }
    }, 350);
  }

  /**
   * Close all menus
   */
  closeAllMenus() {
    this.enhancedMenuItems.forEach((item) => {
      if (item.isOpen) {
        this.closeMenu(item.menuItem);
      }
    });
  }

  /**
   * Setup scroll-close handler
   * Closes any open enhanced megamenu on window scroll.
   */
  setupScrollCloseHandler() {
    this.scrollCloseHandler = () => {
      if (
        !this.activeMenuItem &&
        !this.enhancedMenuItems.some((i) => i.isOpen)
      ) {
        return;
      }
      this.closeAllMenus();
    };
    window.addEventListener("scroll", this.scrollCloseHandler, {
      passive: true,
    });
  }

  /**
   * Setup outside click handler
   */
  setupOutsideClickHandler() {
    this.clickOutsideHandler = (e) => {
      // Check if click is outside all enhanced megamenus
      const isOutside = !e.target.closest(".m-menu__item--mega");

      if (isOutside && this.activeMenuItem) {
        this.closeAllMenus();
      }
    };

    document.addEventListener("click", this.clickOutsideHandler);
  }

  /**
   * Re-initialize sliders if present
   */
  reInitSlider(menuItem) {
    const swiper = menuItem.querySelector(".swiper-container");
    if (!swiper) return;

    const itemIndex = menuItem.dataset.index;
    const slider =
      MinimogTheme &&
      MinimogTheme.headerSliders &&
      MinimogTheme.headerSliders[itemIndex];
    if (slider && slider.update) {
      slider.update();
    }
  }

  /**
   * Destroy and cleanup
   */
  destroy() {
    if (this.clickOutsideHandler) {
      document.removeEventListener("click", this.clickOutsideHandler);
    }
    if (this.scrollCloseHandler) {
      window.removeEventListener("scroll", this.scrollCloseHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
  }
}

// Pre-mark Enhanced Megamenu items BEFORE SiteNav initializes
// This prevents SiteNav from attaching hover events to them
(function preMarkEnhancedItems() {
  const markItems = () => {
    const enhancedItems = document.querySelectorAll(
      ".m-menu__item--mega:has(.m-enhanced-megamenu)"
    );
    enhancedItems.forEach((item) => {
      item.setAttribute("data-enhanced-megamenu-item", "true");
      // Add a class to prevent SiteNav from handling it
      item.classList.add("enhanced-megamenu-controlled");
    });
  };

  // Mark immediately if DOM is ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", markItems);
  } else {
    markItems();
  }
})();

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initEnhancedMegamenu);
} else {
  initEnhancedMegamenu();
}

function initEnhancedMegamenu() {
  const header = document.querySelector("m-header");
  if (header) {
    window.enhancedMegamenu = new EnhancedMegamenu(header);

    // Override SiteNav behavior for Enhanced Megamenu items
    overrideSiteNavForEnhancedItems();
  }
}

/**
 * Override SiteNav event listeners for Enhanced Megamenu items
 * This prevents conflicts with the existing megamenu system.
 *
 * The panel is detached from the DOM at init (see repositionMegamenu) so we
 * can't `newItem.querySelector('.m-enhanced-megamenu')` to find it after
 * cloning — the cloned subtree won't contain it. Instead we read the panel
 * reference from the existing tracking record before cloning, and pass it
 * to `addMenuItem(newItem, cachedPanel)` explicitly.
 */
function overrideSiteNavForEnhancedItems() {
  // Wait for SiteNav to initialize (it runs after mega-menu.js)
  setTimeout(() => {
    const enhancedItems = document.querySelectorAll(
      '.m-menu__item--mega[data-enhanced-megamenu-item="true"]'
    );

    enhancedItems.forEach((item) => {
      const enhancedMegamenu = window.enhancedMegamenu;
      if (!enhancedMegamenu) return;

      // Pull the cached panel BEFORE cloning. If we lose this reference
      // (panel was never tracked), bail out — there's nothing to re-register.
      const tracked = enhancedMegamenu.enhancedMenuItems.find(
        (i) => i.menuItem === item
      );
      const cachedPanel = tracked ? tracked.megamenu : null;

      // Clone and replace the element to remove all event listeners from SiteNav
      const newItem = item.cloneNode(true);
      item.parentNode.replaceChild(newItem, item);

      if (!cachedPanel) return;

      // Drop stale tracking and re-register with the cached panel.
      enhancedMegamenu.enhancedMenuItems =
        enhancedMegamenu.enhancedMenuItems.filter((i) => i.menuItem !== item);
      enhancedMegamenu.addMenuItem(newItem, cachedPanel);
    });

    // After every item has been re-registered, equalise panel heights if the
    // merchant selected "match_tallest" in the section settings.
    const enhancedMegamenu = window.enhancedMegamenu;
    if (enhancedMegamenu) {
      enhancedMegamenu.equalizePanelHeights();
    }
  }, 500); // Wait for SiteNav to initialize
}

// Re-initialize on Shopify theme editor events
if (Shopify && Shopify.designMode) {
  document.addEventListener("shopify:section:load", function (event) {
    if (event.target.querySelector('[data-section-type="header"]')) {
      // Destroy old instance
      if (window.enhancedMegamenu) {
        window.enhancedMegamenu.destroy();
      }
      // Create new instance
      setTimeout(() => {
        initEnhancedMegamenu();
      }, 100);
    }
  });
}
